import { Injectable } from '@angular/core';
import { QueryEntity } from '@datorama/akita';
import { BenefitStore, BenefitState } from './benefit.store';
import { Benefit } from './models/benefit.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BenefitClaimStatus } from './models/benefit-claim-status.model';
import moment from 'moment/moment';

@Injectable({ providedIn: 'root' })
export class BenefitQuery extends QueryEntity<BenefitState> {
  constructor(protected store: BenefitStore) {
    super(store);
  }

  /** All benefits. */
  selectBenefits(): Observable<Benefit[]> {
    return this.selectAll();
  }

  /** All benefits that have been claimed */
  selectClaimedBenefits(): Observable<Benefit[]> {
    return this.selectBenefits().pipe(map((b) => this._getBenefitsClaimed(b)));
  }

  /** The maximum number of benefits the member can claim */
  getClaimLimit(): number | undefined {
    return this.getValue().claimLimit as number;
  }

  /** Whether or not a benefit should be shown */
  shouldShowBenefit(benefit: Benefit, withShownWhenInactive: boolean = true, showClaimed = true): boolean {
    if (
      (this.isActiveBenefit(benefit, showClaimed) || (withShownWhenInactive && benefit.ShowWhenNotActive)) &&
      this.isShowableStatus(benefit, showClaimed)
    ) {
      return true;
    }
    return false;
  }

  /** Check if the benefit is active or not using start and end dates */
  isActiveBenefit(benefit: Benefit, showClaimed: boolean = false): boolean {
    if (moment.utc().isAfter(benefit.StartDate) && moment.utc().isBefore(benefit.EndDate)) {
      return true;
    }

    if (showClaimed) {
      return benefit.Status == BenefitClaimStatus.Unprocessed || benefit.Status == BenefitClaimStatus.Processed;
    }

    return false;
  }

  isShowableStatus(benefit: Benefit, showClaimed = true): boolean {
    switch (benefit.Status) {
      case BenefitClaimStatus.Unclaimed:
        return true;
      case BenefitClaimStatus.Unprocessed:
      case BenefitClaimStatus.Processed:
        return showClaimed;
      case BenefitClaimStatus.Refunded:
        return true;
      case BenefitClaimStatus.Deleted:
        return false;
      case BenefitClaimStatus.OutOfStock:
        return benefit.ShowWhenOutOfStock;
      default:
        return true;
    }
  }

  /** Takes an array of benefits and returns only the ones that have been claimed */
  private _getBenefitsClaimed(benefits: Benefit[]): Benefit[] {
    const claimedStatuses = [BenefitClaimStatus.Processed, BenefitClaimStatus.Unprocessed];
    return benefits.filter((b) => claimedStatuses.includes(b.Status));
  }
}
